import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Checkbox, FormControlLabel, TextField, Paper, LinearProgress, Chip, IconButton,
  Select as MuiSelect, MenuItem, Snackbar, Alert, Box, Tooltip,
  ToggleButton, ToggleButtonGroup
} from '@mui/material';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { collection, doc, getDocs, writeBatch, Timestamp } from 'firebase/firestore';
import { db } from '../../../firebase';
import { getDefaultWeekDates, toCommenceTimeRange } from './dateUtils';
import { reorder, sortReviewGames, isTop25Matchup, defaultScoringType } from '../../../lib/gameOrder';
import { defaultTargetWeek, weekMismatch, findDuplicates, dateSpan, formatSpan } from '../../../lib/importWeek';
import { MAX_WEEK } from './useAdminWeek';

const ImportGamesModal = ({ open, onClose, season, week, teams, existingGames, onGamesImported }) => {
  // Step 1: Browse, Step 2: Review, Step 3: Saving
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [events, setEvents] = useState([]);
  const [isMockData, setIsMockData] = useState(false);
  // Non-fatal enrichment failures reported by fetchApiEvents (spreads,
  // rankings, records). Shown inline so a blank column is never a mystery.
  const [warnings, setWarnings] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [reviewGames, setReviewGames] = useState([]);
  const [targetWeek, setTargetWeek] = useState(() => defaultTargetWeek(week, existingGames, Date.now(), MAX_WEEK));
  // Games already in target weeks other than the working week, read once per
  // week while the modal is open: { [week]: games[] }.
  const [otherWeekGames, setOtherWeekGames] = useState({});
  // Set when the selected games' kickoffs don't match the target week's games.
  const [mismatch, setMismatch] = useState(null);
  const [fromDate, setFromDate] = useState(() => getDefaultWeekDates().from);
  const [toDate, setToDate] = useState(() => getDefaultWeekDates().to);
  const [useMockData, setUseMockData] = useState(false);
  const [sortBy, setSortBy] = useState('time'); // 'time' | 'spread'
  // Default ON: the pool is built around ranked-vs-ranked games, so the browse
  // list opens showing only matchups where BOTH teams are in the Top 25.
  const [top25Only, setTop25Only] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Fetch events when modal opens
  useEffect(() => {
    if (open && step === 1) {
      fetchEvents();
    }
  }, [open]);

  // The parent's week can change any number of times while the modal is
  // closed (it's the page's main navigation now), so seed the target on open.
  // Once every game in the working week has kicked off, the next week is the
  // one being filled. existingGames is deliberately left out of the deps so a
  // parent refetch can't override a week the admin picked.
  useEffect(() => {
    if (open) setTargetWeek(defaultTargetWeek(week, existingGames, Date.now(), MAX_WEEK));
  }, [open, week]);

  const isWorkingWeek = (w) => Number(w) === Number(week);
  // The target week's games: free for the working week, one read otherwise.
  const targetGames = isWorkingWeek(targetWeek) ? existingGames : otherWeekGames[targetWeek];

  useEffect(() => {
    // While saving, the save's own read of the week fills the cache instead.
    if (!open || !season || !targetWeek || isSaving || isWorkingWeek(targetWeek) || otherWeekGames[targetWeek]) return;
    let cancelled = false;
    getDocs(collection(db, 'games', season, targetWeek.toString()))
      .then((snapshot) => {
        if (cancelled) return;
        const games = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        setOtherWeekGames((prev) => ({ ...prev, [targetWeek]: games }));
      })
      .catch((error) => console.error(`Error loading week ${targetWeek} games:`, error));
    return () => { cancelled = true; };
  }, [open, season, targetWeek, week, isSaving]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setStep(1);
      setSelectedIds(new Set());
      setSearchQuery('');
      setReviewGames([]);
      setEvents([]);
      setOtherWeekGames({});
      setMismatch(null);
      const defaults = getDefaultWeekDates();
      setFromDate(defaults.from);
      setToDate(defaults.to);
      setUseMockData(false);
      setSortBy('time');
      setTop25Only(true);
    }
  }, [open]);

  const fetchEvents = async () => {
    setIsLoading(true);
    setSelectedIds(new Set());
    setWarnings([]);
    try {
      const functions = getFunctions();
      const fetchApiEvents = httpsCallable(functions, 'fetchApiEvents');
      let payload;
      if (useMockData) {
        payload = { useMockData: true };
      } else {
        const dateRange = toCommenceTimeRange(fromDate, toDate);
        payload = { commenceTimeFrom: dateRange.from, commenceTimeTo: dateRange.to };
      }
      const result = await fetchApiEvents(payload);
      setEvents(result.data.events || []);
      setIsMockData(result.data.isMockData || false);
      setWarnings(Array.isArray(result.data.warnings) ? result.data.warnings : []);
    } catch (error) {
      console.error('Error fetching events:', error);
      setSnackbar({ open: true, message: 'Failed to fetch events. Using mock data.', severity: 'warning' });
    } finally {
      setIsLoading(false);
    }
  };

  // Check if a game is already imported into the target week
  const isAlreadyImported = (event) => {
    const weekGames = targetGames || [];
    if (weekGames.some(g => g.apiGameId === event.id)) return true;
    // Check by team combo (either arrangement for neutral sites)
    const homeTeam = event.homeFirestoreTeam;
    const awayTeam = event.awayFirestoreTeam;
    if (!homeTeam || !awayTeam) return false;
    return weekGames.some(g =>
      (g.homeTeam === homeTeam.teamId && g.awayTeam === awayTeam.teamId) ||
      (g.homeTeam === awayTeam.teamId && g.awayTeam === homeTeam.teamId)
    );
  };

  // A Top 25 matchup means BOTH teams carry an ESPN ranking (lib/gameOrder).
  // Rankings ride in on the fetched events (resolved server-side by
  // fetchApiEvents), so this filter costs no extra API calls.

  // Filter events by the Top 25 toggle and search query, then sort per the
  // toggle. Sorting only reorders the displayed list — selection state is
  // keyed by event id.
  const byTime = (a, b) => new Date(a.commence_time) - new Date(b.commence_time);
  const bySpread = (a, b) => {
    if (a.spread == null && b.spread == null) return byTime(a, b);
    if (a.spread == null) return 1; // no spread posted sorts last
    if (b.spread == null) return -1;
    return (a.spread - b.spread) || byTime(a, b);
  };
  // Highest-ranked teams first: order by each game's best (lowest-number) rank,
  // then by its other team's rank so ranked-vs-ranked beats ranked-vs-unranked
  // at the same best rank. Fully unranked games sort last, by time.
  const byRanking = (a, b) => {
    const aBest = Math.min(a.homeRanking || Infinity, a.awayRanking || Infinity);
    const bBest = Math.min(b.homeRanking || Infinity, b.awayRanking || Infinity);
    if (aBest !== bBest) return aBest - bBest;
    const aOther = Math.max(a.homeRanking || Infinity, a.awayRanking || Infinity);
    const bOther = Math.max(b.homeRanking || Infinity, b.awayRanking || Infinity);
    if (aOther !== bOther) return aOther - bOther;
    return byTime(a, b);
  };
  const sortComparator =
    sortBy === 'spread' ? bySpread : sortBy === 'ranking' ? byRanking : byTime;
  const filteredEvents = events
    .filter(e => !top25Only || isTop25Matchup(e))
    .filter(e => {
      if (!searchQuery) return true;
      const q = searchQuery.toLowerCase();
      return e.home_team.toLowerCase().includes(q) || e.away_team.toLowerCase().includes(q);
    })
    .sort(sortComparator);

  // Toggle selection
  const toggleSelection = (eventId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(eventId)) next.delete(eventId);
      else next.add(eventId);
      return next;
    });
  };

  const selectAllVisible = () => {
    const ids = filteredEvents
      .filter(e => !isAlreadyImported(e) && e.homeFirestoreTeam && e.awayFirestoreTeam)
      .map(e => e.id);
    setSelectedIds(new Set(ids));
  };

  const deselectAll = () => setSelectedIds(new Set());

  // BYU/Utah games start as Pick the Score (Score + Winner when both teams
  // are ranked); the rest start as Pick the Winner. See lib/gameOrder.
  const withDefaultScoring = (game) => {
    const scoringType = defaultScoringType(game); // 'winner' | 'score' | 'scoreWinner'
    return {
      ...game,
      scoringType,
      isPickScore: scoringType !== 'winner',
      awardWinnerBonus: scoringType === 'scoreWinner',
    };
  };

  // Move to Review step
  const handleNextToReview = () => {
    const selected = events.filter(e => selectedIds.has(e.id));
    const games = selected.map((event) => withDefaultScoring({
      apiGameId: event.id,
      startTime: event.commence_time,
      homeTeamId: event.homeFirestoreTeam?.teamId || '',
      awayTeamId: event.awayFirestoreTeam?.teamId || '',
      homeTeamName: event.homeFirestoreTeam?.teamName || event.home_team,
      awayTeamName: event.awayFirestoreTeam?.teamName || event.away_team,
      homeRanking: event.homeRanking ? String(event.homeRanking) : '',
      awayRanking: event.awayRanking ? String(event.awayRanking) : '',
      homeRecord: event.homeRecord || '',
      awayRecord: event.awayRecord || '',
      neutralSite: '',
    }));
    // Row order in the review table becomes displayOrder on save. Open with
    // the standard order; the admin can re-run it or drag rows to fine-tune.
    setReviewGames(sortReviewGames(games));
    setStep(2);
  };

  // A warning describes the games as they were when Import was clicked.
  useEffect(() => { setMismatch(null); }, [reviewGames]);

  // Update a review game field
  const updateGame = (index, field, value) => {
    setReviewGames(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  // Swap home/away for a game
  const swapTeams = (index) => {
    setReviewGames(prev => {
      const updated = [...prev];
      const g = updated[index];
      updated[index] = {
        ...g,
        homeTeamId: g.awayTeamId,
        awayTeamId: g.homeTeamId,
        homeTeamName: g.awayTeamName,
        awayTeamName: g.homeTeamName,
        homeRanking: g.awayRanking,
        awayRanking: g.homeRanking,
        homeRecord: g.awayRecord,
        awayRecord: g.homeRecord,
      };
      return updated;
    });
  };

  // Set scoring type for all games
  const setBulkScoring = (type) => {
    setReviewGames(prev => prev.map(g => ({ ...g, scoringType: type })));
  };

  // Standard order: pick-the-score games, then Top 25 matchups, then the rest,
  // each by kickoff. Re-run after changing scoring types or rankings.
  const autoSortGames = () => {
    setReviewGames(prev => sortReviewGames(prev));
  };

  // Same drag-and-drop reorder as the admin "Created Games" list, but only in
  // local state — displayOrder is assigned from row position at save time.
  const onReviewDragEnd = (result) => {
    if (!result.destination) return;
    setReviewGames(prev => reorder(prev, result.source.index, result.destination.index));
  };

  const changeTargetWeek = (value) => {
    setTargetWeek(value);
    setMismatch(null);
  };

  // Save games to Firestore. The week is passed in (not read from state) so
  // the mismatch warning's "Import to week N" saves to N in the same click.
  // Unless forced, it first checks the games' kickoffs against the week's
  // existing games, using the read the save makes anyway.
  const saveToWeek = async (saveWeek, { force = false } = {}) => {
    const validGames = reviewGames.filter(g => g.homeTeamId && g.awayTeamId);
    if (validGames.length === 0) {
      setSnackbar({ open: true, message: 'No valid games to import (teams must be mapped).', severity: 'error' });
      return;
    }

    setMismatch(null);
    setIsSaving(true);
    try {
      const weekCollectionRef = collection(db, 'games', season, saveWeek.toString());
      const existingDocs = await getDocs(weekCollectionRef);

      const weekGames = existingDocs.docs.map((d) => d.data());
      if (!isWorkingWeek(saveWeek)) setOtherWeekGames((prev) => ({ ...prev, [saveWeek]: weekGames }));

      // The browse step hides games already in the target week, but the week
      // can change after that (including from the warning below).
      const duplicates = findDuplicates(validGames, weekGames);
      if (duplicates.length > 0) {
        const names = duplicates.map((g) => `${g.awayTeamName} at ${g.homeTeamName}`).join(', ');
        setSnackbar({ open: true, message: `Already in week ${saveWeek}: ${names}. Nothing was imported.`, severity: 'error' });
        return;
      }

      if (!force) {
        const found = weekMismatch(validGames, weekGames, saveWeek, MAX_WEEK);
        if (found) {
          setMismatch({ ...found, week: Number(saveWeek) });
          return;
        }
      }


      // Imported games take their displayOrder from review-row position and
      // append after whatever the target week already holds, so they never
      // collide with (or shuffle) existing games in the player-facing order.
      const maxExistingOrder = existingDocs.docs.reduce((max, d) => {
        const order = Number(d.data().displayOrder);
        return Number.isFinite(order) && order > max ? order : max;
      }, -1);
      const baseOrder = maxExistingOrder + 1;

      const batch = writeBatch(db);

      for (const [position, game] of validGames.entries()) {
        // Firestore's own ids, as Add Game uses. Numbering by count overwrote a
        // game once the week had a gap, and any reused id (a deleted or moved
        // game's) would inherit players' picks still stored under it.
        const gameRef = doc(weekCollectionRef);

        const isPickScore = game.scoringType === 'score' || game.scoringType === 'scoreWinner';
        const awardWinnerBonus = game.scoringType === 'scoreWinner';

        batch.set(gameRef, {
          homeTeam: game.homeTeamId,
          awayTeam: game.awayTeamId,
          homeRanking: game.homeRanking,
          awayRanking: game.awayRanking,
          homeRecord: game.homeRecord,
          awayRecord: game.awayRecord,
          startTime: Timestamp.fromDate(new Date(game.startTime)),
          isPickScore,
          awardWinnerBonus,
          vegasLine: '',
          neutralSite: game.neutralSite,
          displayOrder: baseOrder + position,
          apiGameId: game.apiGameId,
          week: parseInt(saveWeek),
        });
      }

      await batch.commit();
      setSnackbar({ open: true, message: `${validGames.length} game(s) imported successfully!`, severity: 'success' });

      // Refresh parent and close
      if (onGamesImported) onGamesImported();
      setTimeout(() => onClose(), 1500);
    } catch (error) {
      console.error('Error saving games:', error);
      setSnackbar({ open: true, message: `Failed to save: ${error.message}`, severity: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = () => saveToWeek(targetWeek);

  const switchToSuggestedWeek = () => {
    const suggested = mismatch.suggestedWeek;
    setTargetWeek(suggested);
    saveToWeek(suggested);
  };

  // Week dropdown labels show the kickoff dates of weeks already loaded (the
  // working week, plus any target week read above), e.g. "6 · Oct 9–10".
  const weekLabel = (w) => {
    const games = isWorkingWeek(w) ? existingGames : otherWeekGames[w];
    const span = formatSpan(dateSpan(games));
    return span ? `${w} · ${span}` : String(w);
  };

  const formatDateTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true,
    });
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          {step === 1 ? 'Import Games from API' : 'Review & Edit Games'}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
            <Typography variant="body2" color="text.secondary">Import to Week:</Typography>
            <MuiSelect
              size="small"
              value={targetWeek}
              onChange={(e) => changeTargetWeek(e.target.value)}
              sx={{ minWidth: 70 }}
              inputProps={{ 'aria-label': 'Import to week' }}
            >
              {Array.from({ length: MAX_WEEK }, (_, i) => (
                <MenuItem key={i + 1} value={i + 1}>{weekLabel(i + 1)}</MenuItem>
              ))}
            </MuiSelect>
          </Box>
          {isMockData && step === 1 && (
            <Chip label="Mock Data (Off-Season)" color="warning" size="small" />
          )}
        </DialogTitle>

        <DialogContent dividers>
          {/* ── Step 1: Browse ── */}
          {step === 1 && (
            <>
              {warnings.length > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }} data-testid="import-warnings">
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Some game details could not be loaded:</Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                    {warnings.map((warning) => (
                      <li key={warning}><Typography variant="body2">{warning}</Typography></li>
                    ))}
                  </Box>
                </Alert>
              )}
              <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography variant="body2" color="text.secondary">Date Range:</Typography>
                <TextField
                  size="small"
                  type="date"
                  label="From"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
                <TextField
                  size="small"
                  type="date"
                  label="To"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
                <Button
                  size="small"
                  variant="outlined"
                  onClick={fetchEvents}
                  disabled={isLoading || (!useMockData && (!fromDate || !toDate || fromDate > toDate))}
                >
                  Fetch Games
                </Button>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={useMockData}
                      onChange={(e) => setUseMockData(e.target.checked)}
                    />
                  }
                  label={<Typography variant="body2">Use mock data (testing)</Typography>}
                />
                {!useMockData && fromDate && toDate && fromDate > toDate && (
                  <Typography variant="body2" color="error">From must be on or before To</Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField
                  size="small"
                  placeholder="Search by team name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  sx={{ flex: 1, minWidth: 200 }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={top25Only}
                      onChange={(e) => setTop25Only(e.target.checked)}
                    />
                  }
                  label={<Typography variant="body2">Top 25 matchups only</Typography>}
                />
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={sortBy}
                  onChange={(e, value) => { if (value) setSortBy(value); }}
                >
                  <ToggleButton value="time" sx={{ textTransform: 'none', px: 1.5 }}>Game Time</ToggleButton>
                  <ToggleButton value="spread" sx={{ textTransform: 'none', px: 1.5 }}>Closest Spread</ToggleButton>
                  <ToggleButton value="ranking" sx={{ textTransform: 'none', px: 1.5 }}>Top Ranked</ToggleButton>
                </ToggleButtonGroup>
                <Button size="small" variant="text" onClick={selectAllVisible}>Select All Mapped</Button>
                <Button size="small" variant="text" onClick={deselectAll}>Deselect All</Button>
                <Typography variant="body2" color="text.secondary">
                  {selectedIds.size} selected
                </Typography>
              </Box>

              {isLoading ? (
                <LinearProgress />
              ) : (
                <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox" />
                        <TableCell>Game Time</TableCell>
                        <TableCell>Away Team</TableCell>
                        <TableCell align="center">@</TableCell>
                        <TableCell>Home Team</TableCell>
                        <TableCell align="center">Spread</TableCell>
                        <TableCell align="center">Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredEvents.map((event) => {
                        const imported = isAlreadyImported(event);
                        const bothMapped = event.homeFirestoreTeam && event.awayFirestoreTeam;
                        return (
                          <TableRow
                            key={event.id}
                            sx={{ opacity: imported ? 0.4 : 1, bgcolor: imported ? '#f5f5f5' : 'inherit' }}
                          >
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={selectedIds.has(event.id)}
                                onChange={() => toggleSelection(event.id)}
                                disabled={imported}
                              />
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.85em', whiteSpace: 'nowrap' }}>
                              {formatDateTime(event.commence_time)}
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {event.awayFirestoreTeam ? (
                                  <Tooltip title={`Mapped: ${event.awayFirestoreTeam.teamName}`}>
                                    <CheckCircleIcon sx={{ fontSize: 16, color: '#2e7d32' }} />
                                  </Tooltip>
                                ) : (
                                  <Tooltip title="Team not mapped in BigBoard">
                                    <WarningIcon sx={{ fontSize: 16, color: '#ed6c02' }} />
                                  </Tooltip>
                                )}
                                {event.awayRanking ? `#${event.awayRanking} ${event.away_team}` : event.away_team}
                              </Box>
                            </TableCell>
                            <TableCell align="center">@</TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {event.homeFirestoreTeam ? (
                                  <Tooltip title={`Mapped: ${event.homeFirestoreTeam.teamName}`}>
                                    <CheckCircleIcon sx={{ fontSize: 16, color: '#2e7d32' }} />
                                  </Tooltip>
                                ) : (
                                  <Tooltip title="Team not mapped in BigBoard">
                                    <WarningIcon sx={{ fontSize: 16, color: '#ed6c02' }} />
                                  </Tooltip>
                                )}
                                {event.homeRanking ? `#${event.homeRanking} ${event.home_team}` : event.home_team}
                              </Box>
                            </TableCell>
                            <TableCell align="center" sx={{ fontSize: '0.85em', whiteSpace: 'nowrap' }}>
                              {event.spreadDisplay || '—'}
                            </TableCell>
                            <TableCell align="center">
                              {imported ? (
                                <Chip label="Imported" size="small" color="default" />
                              ) : bothMapped ? (
                                <Chip label="Ready" size="small" color="success" variant="outlined" />
                              ) : (
                                <Chip label="Unmapped" size="small" color="warning" variant="outlined" />
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {filteredEvents.length === 0 && !isLoading && (
                        <TableRow>
                          <TableCell colSpan={7} align="center">
                            <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                              {top25Only && events.length > 0
                                ? `No Top 25 matchups in this date range — untick "Top 25 matchups only" to see all ${events.length} game(s).`
                                : 'No events found for the selected date range.'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}

          {/* ── Step 2: Review ── */}
          {step === 2 && (
            <>
              <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mr: 1, alignSelf: 'center' }}>
                  Bulk scoring:
                </Typography>
                <Button size="small" variant="outlined" onClick={() => setBulkScoring('winner')}>All Winner</Button>
                <Button size="small" variant="outlined" onClick={() => setBulkScoring('score')}>All Pick Score</Button>
                <Button size="small" variant="outlined" onClick={() => setBulkScoring('scoreWinner')}>All Score + Winner</Button>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', ml: 'auto' }}>
                  <Tooltip title="Pick-the-score games first, then Top 25 matchups, then the rest — each by kickoff time">
                    <Button size="small" variant="outlined" onClick={autoSortGames}>Auto Sort</Button>
                  </Tooltip>
                  <Typography variant="body2" color="text.secondary">
                    Drag rows to set the display order.
                  </Typography>
                </Box>
              </Box>

              <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
                <DragDropContext onDragEnd={onReviewDragEnd}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>#</TableCell>
                        <TableCell>Game Time</TableCell>
                        <TableCell>Away Team</TableCell>
                        <TableCell>Away Rank</TableCell>
                        <TableCell>Away Record</TableCell>
                        <TableCell align="center">Swap</TableCell>
                        <TableCell>Home Team</TableCell>
                        <TableCell>Home Rank</TableCell>
                        <TableCell>Home Record</TableCell>
                        <TableCell>Scoring</TableCell>
                        <TableCell>Neutral Site</TableCell>
                      </TableRow>
                    </TableHead>
                    <Droppable droppableId="review-games">
                      {(droppable) => (
                      <TableBody ref={droppable.innerRef} {...droppable.droppableProps}>
                        {reviewGames.map((game, index) => (
                          <Draggable key={game.apiGameId} draggableId={String(game.apiGameId)} index={index}>
                            {(draggable, snapshot) => (
                              <TableRow
                                ref={draggable.innerRef}
                                {...draggable.draggableProps}
                                sx={{ bgcolor: snapshot.isDragging ? 'action.hover' : 'inherit' }}
                              >
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Tooltip title="Drag to reorder">
                                      <Box
                                        component="span"
                                        aria-label={`Drag to reorder ${game.awayTeamName} at ${game.homeTeamName}`}
                                        {...draggable.dragHandleProps}
                                        sx={{ display: 'inline-flex', cursor: 'grab', color: 'text.secondary' }}
                                      >
                                        <DragIndicatorIcon fontSize="small" />
                                      </Box>
                                    </Tooltip>
                                    {index + 1}
                                  </Box>
                                </TableCell>
                                <TableCell sx={{ fontSize: '0.8em', whiteSpace: 'nowrap' }}>
                                  {formatDateTime(game.startTime)}
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600, fontSize: '0.85em' }}>
                                  {game.awayTeamName}
                                  {!game.awayTeamId && <WarningIcon sx={{ fontSize: 14, color: '#ed6c02', ml: 0.5 }} />}
                                </TableCell>
                                <TableCell>
                                  <TextField size="small" placeholder="#" value={game.awayRanking}
                                    onChange={(e) => updateGame(index, 'awayRanking', e.target.value)}
                                    sx={{ width: 50 }} inputProps={{ style: { fontSize: '0.85em' } }}
                                  />
                                </TableCell>
                                <TableCell>
                                  <TextField size="small" placeholder="0-0" value={game.awayRecord}
                                    onChange={(e) => updateGame(index, 'awayRecord', e.target.value)}
                                    sx={{ width: 60 }} inputProps={{ style: { fontSize: '0.85em' } }}
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  <Tooltip title="Swap Home/Away">
                                    <IconButton size="small" onClick={() => swapTeams(index)}>
                                      <SwapHorizIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600, fontSize: '0.85em' }}>
                                  {game.homeTeamName}
                                  {!game.homeTeamId && <WarningIcon sx={{ fontSize: 14, color: '#ed6c02', ml: 0.5 }} />}
                                </TableCell>
                                <TableCell>
                                  <TextField size="small" placeholder="#" value={game.homeRanking}
                                    onChange={(e) => updateGame(index, 'homeRanking', e.target.value)}
                                    sx={{ width: 50 }} inputProps={{ style: { fontSize: '0.85em' } }}
                                  />
                                </TableCell>
                                <TableCell>
                                  <TextField size="small" placeholder="0-0" value={game.homeRecord}
                                    onChange={(e) => updateGame(index, 'homeRecord', e.target.value)}
                                    sx={{ width: 60 }} inputProps={{ style: { fontSize: '0.85em' } }}
                                  />
                                </TableCell>
                                <TableCell>
                                  <MuiSelect size="small" value={game.scoringType}
                                    onChange={(e) => updateGame(index, 'scoringType', e.target.value)}
                                    sx={{ fontSize: '0.8em', minWidth: 100 }}
                                  >
                                    <MenuItem value="winner">Winner</MenuItem>
                                    <MenuItem value="score">Pick Score</MenuItem>
                                    <MenuItem value="scoreWinner">Score + Winner</MenuItem>
                                  </MuiSelect>
                                </TableCell>
                                <TableCell>
                                  <TextField size="small" placeholder="Venue" value={game.neutralSite}
                                    onChange={(e) => updateGame(index, 'neutralSite', e.target.value)}
                                    sx={{ width: 120 }} inputProps={{ style: { fontSize: '0.85em' } }}
                                  />
                                </TableCell>
                              </TableRow>
                            )}
                          </Draggable>
                        ))}
                        {droppable.placeholder}
                      </TableBody>
                      )}
                    </Droppable>
                  </Table>
                </DragDropContext>
              </TableContainer>
            </>
          )}
        </DialogContent>

        {/* Outside the scrolling content so it shows next to the Import button. */}
        {step === 2 && mismatch && (
          <Alert severity="warning" data-testid="week-mismatch" sx={{ mx: 2, mt: 2 }}>
            <Typography variant="body2">
              {`These games are ${formatSpan(mismatch.importSpan)}; week ${mismatch.week} games are ${formatSpan(mismatch.targetSpan)}.`}
              {mismatch.suggestedWeek && ` Import to week ${mismatch.suggestedWeek} instead?`}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
              {mismatch.suggestedWeek && (
                <Button size="small" variant="contained" color="warning" onClick={switchToSuggestedWeek} disabled={isSaving}>
                  {`Import to week ${mismatch.suggestedWeek}`}
                </Button>
              )}
              <Button size="small" color="inherit" onClick={() => saveToWeek(mismatch.week, { force: true })} disabled={isSaving}>
                {`Import to week ${mismatch.week} anyway`}
              </Button>
            </Box>
          </Alert>
        )}

        <DialogActions>
          {step === 1 && (
            <>
              <Button onClick={onClose}>Cancel</Button>
              <Button
                variant="contained"
                onClick={handleNextToReview}
                disabled={selectedIds.size === 0}
              >
                Next: Review Selected ({selectedIds.size})
              </Button>
            </>
          )}
          {step === 2 && (
            <>
              <Button onClick={() => { setMismatch(null); setStep(1); }} disabled={isSaving}>Back</Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSave}
                disabled={reviewGames.filter(g => g.homeTeamId && g.awayTeamId).length === 0}
                loading={isSaving}
              >
                {`Import ${reviewGames.filter(g => g.homeTeamId && g.awayTeamId).length} Game(s)`}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ImportGamesModal;
